/**
 * AI Context Provider
 * Provides AI service status and utilities across the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { aiService } from '../../services/ai';
import { AIServiceStatus, AIFeatureStatus } from '../../services/ai/ai.types';
import { AuthService } from '../../services/auth.service';

// ============================================================================
// Context Types
// ============================================================================

interface AIContextState {
  // Status
  status: AIServiceStatus | null;
  isLoading: boolean;
  error: string | null;

  // Feature checks
  isAIEnabled: boolean;
  isLocalLLMEnabled: boolean;
  isLocalLLMHealthy: boolean;
  features: AIFeatureStatus | null;

  // Actions
  refreshStatus: () => Promise<void>;
  isFeatureEnabled: (feature: keyof AIFeatureStatus) => boolean;
}

const defaultFeatures: AIFeatureStatus = {
  testCaseGeneration: false,
  dataSuggestion: false,
  negativeTestGeneration: false,
  apiTestGeneration: false,
  webElementAnalysis: false,
  adaptiveLearning: false,
  testSuiteGrouping: false,
  flowGeneration: false,
  manualToAutomation: false
};

const defaultContext: AIContextState = {
  status: null,
  isLoading: true,
  error: null,
  isAIEnabled: false,
  isLocalLLMEnabled: false,
  isLocalLLMHealthy: false,
  features: null,
  refreshStatus: async () => {},
  isFeatureEnabled: () => false
};

// ============================================================================
// Context
// ============================================================================

const AIContext = createContext<AIContextState>(defaultContext);

// ============================================================================
// Provider
// ============================================================================

interface AIProviderProps {
  children: ReactNode;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const AIProvider: React.FC<AIProviderProps> = ({
  children,
  autoRefresh = true,
  refreshInterval = 60000 // 1 minute
}) => {
  const [status, setStatus] = useState<AIServiceStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = React.useRef(false);

  const fetchStatus = useCallback(async (forceRefresh: boolean) => {
    // Skip AI status check if user is not authenticated
    const token = AuthService.token;
    if (!token) {
      setIsLoading(false);
      setStatus(null);
      return;
    }

    try {
      if (!hasLoadedRef.current) setIsLoading(true); // Only show loading on first fetch
      setError(null);
      const newStatus = await aiService.getStatus(forceRefresh);
      setStatus(newStatus);
      hasLoadedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch AI status');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Public refresh (force bypasses cache)
  const refreshStatus = useCallback(async () => {
    await fetchStatus(true);
  }, [fetchStatus]);

  // Initial fetch (uses cache — prevents duplicate calls on rapid mount/remount)
  useEffect(() => {
    fetchStatus(false);
  }, [fetchStatus]);

  // Auto-refresh if enabled (uses cache TTL — won't double-fetch within 60s)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => fetchStatus(false), refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchStatus]);

  // Derived state
  const isAIEnabled = status?.aiEnabled || false;
  const isLocalLLMEnabled = status?.localLLM?.enabled || false;
  const isLocalLLMHealthy = status?.localLLM?.healthy || false;
  const features = status?.localLLM?.features || null;

  const isFeatureEnabled = useCallback((feature: keyof AIFeatureStatus): boolean => {
    if (!isAIEnabled || !isLocalLLMEnabled) return false;
    return features?.[feature] || false;
  }, [isAIEnabled, isLocalLLMEnabled, features]);

  const value: AIContextState = {
    status,
    isLoading,
    error,
    isAIEnabled,
    isLocalLLMEnabled,
    isLocalLLMHealthy,
    features,
    refreshStatus,
    isFeatureEnabled
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
};

// ============================================================================
// Hook
// ============================================================================

export const useAI = (): AIContextState => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
};

// ============================================================================
// Feature-specific Hooks
// ============================================================================

/**
 * Hook for test generation feature
 */
export const useTestGeneration = () => {
  const { isFeatureEnabled, isLocalLLMHealthy } = useAI();
  const isEnabled = isFeatureEnabled('testCaseGeneration');

  return {
    isEnabled,
    isAvailable: isEnabled && isLocalLLMHealthy
  };
};

/**
 * Hook for data suggestion feature
 */
export const useDataSuggestion = () => {
  const { isFeatureEnabled, isLocalLLMHealthy } = useAI();
  const isEnabled = isFeatureEnabled('dataSuggestion');

  return {
    isEnabled,
    isAvailable: isEnabled && isLocalLLMHealthy
  };
};

/**
 * Hook for web analysis feature
 */
export const useWebAnalysis = () => {
  const { isFeatureEnabled, isLocalLLMHealthy } = useAI();
  const isEnabled = isFeatureEnabled('webElementAnalysis');

  return {
    isEnabled,
    isAvailable: isEnabled && isLocalLLMHealthy
  };
};

export default AIContext;
